// SCR-OFF-02 and SCR-OFF-03 (frozen mockups-off-v1): the sales and collections waiting on the phone, in the order they
// will be sent, with their temporary numbers. A refused item comes first and holds the rest; the Seller edits or deletes
// it and the queue goes on (ADR-128).
import { useCallback, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge, BottomSheet, Button, EmptyState, List, ListRow, Notice, SheetActions } from '../../design/kit';
import { flushOutbox, outbox, setResume, useOutbox } from '../../data/offline';
import type { OutboxItem } from '../../lib/offline/outbox';
import { Code, Name } from '../../i18n/Isolate';
import { formatDateTime, formatMoney } from '../../lib/format';
import type { Language } from '../../i18n';
import { messageKey } from '../../lib/errors';
import { useCardHeader } from '../../app/Shell';

export function QueuePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const navigate = useNavigate();
  const box = useOutbox();
  const [open, setOpen] = useState<OutboxItem | null>(null);
  const back = useCallback(() => navigate('/round'), [navigate]);
  useCardHeader(t('q.title'), back);
  if (box.items.length === 0) return <EmptyState icon="check" title={t('q.empty')} text={t('q.empty.text')} />;
  const status = (i: OutboxItem) =>
    i.status === 'refused' ? (
      <Badge tone="danger">{t('q.refused')}</Badge>
    ) : box.held ? (
      <Badge>{t('q.blocked')}</Badge>
    ) : i.status === 'sending' ? (
      <Badge tone="info">{t('q.sending')}</Badge>
    ) : (
      <Badge tone="info">{t('q.waiting')}</Badge>
    );
  return (
    <>
      <Notice tone="info">{t('q.hint')}</Notice>
      <List label={t('q.title')}>
        {box.items.map((i) => (
          <ListRow
            key={i.id}
            testId={`queue-${i.pending}`}
            lead={{ icon: i.kind === 'sale' ? 'file' : 'cash' }}
            title={<Name>{i.customer}</Name>}
            sub={
              <>
                <Code>{t('q.pending', { n: i.pending })}</Code> · {formatDateTime(i.createdAt, lang)}
                <br />
                {t(i.kind === 'sale' ? 'q.sale' : 'q.collection')}
                <br />
                {status(i)}
              </>
            }
            value={formatMoney(i.total, lang)}
            actions={
              i.status === 'refused' ? (
                <Button pill onClick={() => setOpen(i)}>
                  {t('rf.edit')}
                </Button>
              ) : i.status === 'waiting' ? (
                <Button pill onClick={() => void outbox.remove(i.id)}>
                  {t('q.delete')}
                </Button>
              ) : undefined
            }
          />
        ))}
      </List>
      {open && <RefusedSheet item={open} onClose={() => setOpen(null)} />}
    </>
  );
}

/** SCR-OFF-03: why the server refused, then edit the sale or delete it; the rest of the queue continues. */
function RefusedSheet({ item, onClose }: { item: OutboxItem; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sure, setSure] = useState(false);
  const reason = item.refusal ? refusalText(t, item.refusal.code, item.refusal.detail) : '';
  const name = t('q.pending', { n: item.pending });
  return (
    <BottomSheet title={t('rf.title', { n: name })} open onClose={onClose}>
      <div className="yv-skeleton-stack">
        <Notice tone="danger">
          <b>{t('rf.reason')}</b> · {reason}
        </Notice>
        {sure ? (
          <>
            <Notice tone="warning">{t('rf.delconfirm', { n: name })}</Notice>
            <SheetActions>
              <Button
                variant="danger"
                onClick={() =>
                  void outbox.remove(item.id).then(() => {
                    onClose();
                    void flushOutbox();
                  })
                }
              >
                {t('rf.del')}
              </Button>
              <Button onClick={() => setSure(false)}>{t('cancel')}</Button>
            </SheetActions>
          </>
        ) : (
          <>
            <p className="muted">{t('rf.rest')}</p>
            <SheetActions>
              {item.kind === 'sale' && (
                <Button
                  variant="main"
                  onClick={() => {
                    const p = item.params as { p_customer: string; p_lines: unknown[] };
                    void outbox.takeForEdit(item.id).then(() => {
                      setResume({ customer: p.p_customer, lines: p.p_lines });
                      onClose();
                      navigate('/round');
                    });
                  }}
                >
                  {t('rf.edit')}
                </Button>
              )}
              <Button variant="danger" onClick={() => setSure(true)}>
                {t('rf.del')}
              </Button>
            </SheetActions>
          </>
        )}
      </div>
    </BottomSheet>
  );
}

function refusalText(t: TFunction, code: string, detail: Record<string, unknown>): string {
  if (code === 'NOT_ENOUGH_STOCK') {
    const loose = Number(detail.loose ?? 0) + Number(detail.boxes ?? 0) * Number(detail.capacity ?? 0);
    return t('rf.stock', { n: loose });
  }
  return t(messageKey(code), { p: String(detail.product ?? ''), c: String(detail.customer ?? '') });
}
